import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const { user: currentUser } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/analytics/site?timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (format = 'json') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/export?timeRange=${timeRange}&format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics_export_${timeRange}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics data...</p>
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h1>📊 Business Analytics Dashboard</h1>
        <div className="analytics-controls">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <button onClick={() => exportData('json')} className="export-btn">
            📥 Export JSON
          </button>
          <button onClick={() => exportData('csv')} className="export-btn">
            📊 Export CSV
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {analyticsData?.overview && (
        <div className="overview-cards">
          <div className="metric-card">
            <h3>Total Users</h3>
            <div className="metric-value">{analyticsData.overview.totalUsers}</div>
            <div className="metric-change">+{analyticsData.overview.newUsers} new</div>
          </div>
          <div className="metric-card">
            <h3>Active Users</h3>
            <div className="metric-value">{analyticsData.overview.activeUsers}</div>
            <div className="metric-change">{analyticsData.overview.averageEngagement}% engaged</div>
          </div>
          <div className="metric-card">
            <h3>Conversion Rate</h3>
            <div className="metric-value">{analyticsData.overview.conversionRate}%</div>
            <div className="metric-change">From visitors to actions</div>
          </div>
          <div className="metric-card">
            <h3>Revenue</h3>
            <div className="metric-value">${analyticsData.overview.totalRevenue}</div>
            <div className="metric-change">${analyticsData.overview.monthlyRecurringRevenue}/mo MRR</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="analytics-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={activeTab === 'workspaces' ? 'active' : ''}
          onClick={() => setActiveTab('workspaces')}
        >
          Workspaces
        </button>
        <button
          className={activeTab === 'revenue' ? 'active' : ''}
          onClick={() => setActiveTab('revenue')}
        >
          Revenue
        </button>
        <button
          className={activeTab === 'insights' ? 'active' : ''}
          onClick={() => setActiveTab('insights')}
        >
          Insights
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && analyticsData && (
          <div className="overview-tab">
            <div className="chart-grid">
              {/* Plan Distribution */}
              <div className="chart-card">
                <h3>Plan Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(analyticsData.plans).map(([plan, data]) => ({
                        name: plan.charAt(0).toUpperCase() + plan.slice(1),
                        value: data.users,
                        percentage: data.percentage
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {Object.entries(analyticsData.plans).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Workspace Usage */}
              <div className="chart-card">
                <h3>Workspace Usage</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={Object.entries(analyticsData.workspaces).map(([workspace, data]) => ({
                    name: workspace.charAt(0).toUpperCase() + workspace.slice(1),
                    users: data.users,
                    engagement: parseFloat(data.engagement)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="users" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && analyticsData?.users && (
          <div className="users-tab">
            <div className="chart-grid">
              <div className="chart-card">
                <h3>User Growth</h3>
                <div className="user-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Users:</span>
                    <span className="stat-value">{analyticsData.users.totalUsers}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Active Users:</span>
                    <span className="stat-value">{analyticsData.users.activeUsers}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">New Users:</span>
                    <span className="stat-value">{analyticsData.users.newUsers}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Activity Rate:</span>
                    <span className="stat-value">{analyticsData.users.activityRate}%</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>User Roles Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={Object.entries(analyticsData.users.roleDistribution).map(([role, count]) => ({
                    role: role.charAt(0).toUpperCase() + role.slice(1),
                    count
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="role" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workspaces' && analyticsData?.workspaces && (
          <div className="workspaces-tab">
            <div className="workspace-grid">
              {Object.entries(analyticsData.workspaces).map(([workspace, data]) => (
                <div key={workspace} className="workspace-card">
                  <h3>{workspace.charAt(0).toUpperCase() + workspace.slice(1)} Workspace</h3>
                  <div className="workspace-stats">
                    <div className="stat">
                      <span>Users:</span>
                      <strong>{data.users}</strong>
                    </div>
                    <div className="stat">
                      <span>Interactions:</span>
                      <strong>{data.interactions}</strong>
                    </div>
                    <div className="stat">
                      <span>Engagement Score:</span>
                      <strong>{data.engagement}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'revenue' && analyticsData?.revenue && (
          <div className="revenue-tab">
            <div className="chart-grid">
              <div className="chart-card">
                <h3>Revenue Breakdown</h3>
                <div className="revenue-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Revenue:</span>
                    <span className="stat-value">${analyticsData.revenue.totalRevenue}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Monthly Recurring Revenue:</span>
                    <span className="stat-value">${analyticsData.revenue.mrr}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Avg Revenue per User:</span>
                    <span className="stat-value">${analyticsData.revenue.averageRevenuePerUser}</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Revenue by Plan</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={Object.entries(analyticsData.revenue.revenueByPlan).map(([plan, amount]) => ({
                    plan: plan.charAt(0).toUpperCase() + plan.slice(1),
                    revenue: parseFloat(amount)
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="plan" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                    <Bar dataKey="revenue" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'insights' && analyticsData && (
          <div className="insights-tab">
            <div className="insights-section">
              <h3>🤖 AI-Generated Insights</h3>
              <div className="insights-list">
                {analyticsData.insights?.map((insight, index) => (
                  <div key={index} className={`insight-card ${insight.impact}`}>
                    <div className="insight-header">
                      <span className="insight-type">{insight.type.toUpperCase()}</span>
                      <span className="insight-impact">{insight.impact} impact</span>
                    </div>
                    <h4>{insight.title}</h4>
                    <p>{insight.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="recommendations-section">
              <h3>💡 Business Recommendations</h3>
              <div className="recommendations-list">
                {analyticsData.recommendations?.map((rec, index) => (
                  <div key={index} className={`recommendation-card ${rec.priority}`}>
                    <div className="rec-header">
                      <span className="rec-category">{rec.category}</span>
                      <span className="rec-priority">{rec.priority} priority</span>
                    </div>
                    <h4>{rec.title}</h4>
                    <p>{rec.description}</p>
                    <div className="expected-impact">
                      Expected Impact: {rec.expectedImpact}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
